import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Video, Sparkles, X, ChevronRight, Upload } from "lucide-react";
import EndLessonClipUploader from "../components/EndLessonClipUploader";
import { MEDIA_FEATURE_ENABLED } from "../lib/mediaFeatureFlag";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LearnerPendingUploadsPrompt — learner-facing lesson-end loop closer.
 *
 * Pulls paid-but-not-uploaded Passport add-ons and surfaces a single,
 * zero-friction CTA on the learner home:
 *
 *   "Upload your paid clip from <date>" → opens EndLessonClipUploader
 *     → picks file → uploads → addon flips to consumed → AI review fires
 *     (for premium_review) → card updates to next pending or hides.
 *
 * Designed to mount once on LearnerHub. No props required.
 */
export default function LearnerPendingUploadsPrompt() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeAddon, setActiveAddon] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    // Media quarantine: never call the quarantined add-ons endpoint
    // while the gate is closed. Effect early-returns so no network
    // call is made and the widget renders null below.
    if (!MEDIA_FEATURE_ENABLED) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/passport/addons/my-pending`,
        { withCredentials: true },
      );
      setItems(data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Media quarantine — silent. Mounted but renders nothing; no
  // background polling, no dead CTAs.
  if (!MEDIA_FEATURE_ENABLED) return null;

  if (loading || dismissed || items.length === 0) return null;

  const head = items[0];
  const more = items.length - 1;
  const premium = head.product === "premium_review";

  const dateLabel = head.lesson_date
    ? new Date(head.lesson_date + "T00:00:00").toLocaleDateString("en-GB", {
        weekday: "short", day: "numeric", month: "short",
      })
    : "a recent lesson";

  if (activeAddon) {
    return (
      <EndLessonClipUploader
        addonId={activeAddon.addon_id}
        product={activeAddon.product}
        onComplete={async () => {
          toast.success("Uploaded — saved to your Passport");
          setActiveAddon(null);
          await load();
        }}
        onSkip={() => setActiveAddon(null)}
      />
    );
  }

  return (
    <div
      data-testid="learner-pending-uploads-prompt"
      className="relative rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/10 p-4"
    >
      <button
        data-testid="dismiss-pending-uploads"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-slate-800/50"
      >
        <X className="w-4 h-4 text-slate-400" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
            premium ? "bg-slate-900 dark:bg-white" : "bg-indigo-600"
          }`}
        >
          {premium ? (
            <Sparkles className="w-5 h-5 text-white dark:text-slate-900" />
          ) : (
            <Video className="w-5 h-5 text-white" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">
            {premium ? "Your Premium Review is ready to generate" : "You have a paid clip waiting to upload"}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {head.product_label} · £{(head.price_pence / 100).toFixed(0)} · {dateLabel}
            {head.instructor_name ? ` · ${head.instructor_name}` : ""}
          </p>
          {premium && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 italic">
              Upload your clip — AI review is guidance only, not a pass prediction.
            </p>
          )}
          {more > 0 && (
            <p
              data-testid="pending-uploads-more"
              className="text-[11px] text-indigo-700 dark:text-indigo-300 mt-1 font-bold"
            >
              +{more} more pending upload{more === 1 ? "" : "s"}
            </p>
          )}

          <button
            data-testid="open-pending-uploader"
            onClick={() => setActiveAddon(head)}
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold active:scale-95"
          >
            <Upload className="w-3.5 h-3.5" />
            Upload now
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
